/**
 * Order sheet table organism: displays rezept sheets with checkboxes, quantity, delivery date, etc.
 */

import { useMemo } from 'react';
import { AppCheckbox, AppSelect, AppTable, AppTextField } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import type { OrderSheetFormHandlers } from '@/hooks/main/useOrderSheet';
import type { OrderSheetRowState, RezeptSheetItem } from '@/pages/main/orderSheet/types';
import { QUANTITY_OPTIONS } from '@/pages/main/orderSheet/constants';
import { Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

const dayPickerFormat = parseFormat(JA_JP.format);

function formatUnitAmount(value: number | string | null | undefined): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? ` ${Math.trunc(parsed)}枚` : '';
}

/** Convert RA2NDatePicker value (和暦 or Y-m-d) to Y-m-d. Returns null if empty/invalid. */
function dateValueToYmd(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const d = parseDate(value.trim(), dayPickerFormat);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface OrderSheetTableProps {
  sheets: RezeptSheetItem[];
  rowState: Record<number, OrderSheetRowState>;
  deliveryFeeMaster: number;
  handlers: OrderSheetFormHandlers;
  subtotal: number;
  buildInitialRowState: () => OrderSheetRowState;
}

export default function OrderSheetTable({
  sheets,
  rowState,
  handlers,
  subtotal,
  buildInitialRowState,
}: OrderSheetTableProps) {
  const {
    handleSheetCheck,
    handleQuantityChange,
    handleDeliveryDateChange,
    handleIncludeCaseCheck,
    handleRemarksChange,
  } = handlers;

  const columns: ColumnsType<RezeptSheetItem> = useMemo(
    () => [
      {
        key: 'check',
        title: '',
        width: 56,
        align: 'center',
        render: (_, record) => {
          const row = rowState[record.id] ?? buildInitialRowState();
          return (
            <AppCheckbox
              checked={row.checked}
              onChange={e => handleSheetCheck(record, e.target.checked)}
              aria-label={`用紙 ${record.sheet_name} を選択`}
            />
          );
        },
      },
      {
        key: 'sheet',
        title: '用紙',
        render: (_, record) => `${record.sheet_name}${formatUnitAmount(record.unit_amount)}`,
      },
      {
        key: 'price',
        title: '単価(税込)',
        align: 'right',
        render: (_, record) => `${record.price.toLocaleString()}円`,
      },
      {
        key: 'quantity',
        title: '数量',
        width: 100,
        render: (_, record) => {
          const row = rowState[record.id] ?? buildInitialRowState();
          return (
            <AppSelect
              value={row.quantity}
              onChange={(val: unknown) =>
                handleQuantityChange(record.id, val as number | undefined)
              }
              options={QUANTITY_OPTIONS}
              disabled={!row.checked}
              className="w-full"
            />
          );
        },
      },
      {
        key: 'request_delivery_date',
        title: '納品希望日',
        width: 180,
        render: (_, record) => {
          const row = rowState[record.id] ?? buildInitialRowState();
          return (
            <RA2NDatePicker
              key={`dp-${record.id}-${row.datePickerKey}`}
              picker="day"
              value={row.requestDeliveryDate ?? ''}
              onChange={v => {
                const dateStr = dateValueToYmd(v);
                handleDeliveryDateChange(record.id, dateStr);
              }}
              disabled={!row.checked || row.isIncludeCase}
              className="w-full"
            />
          );
        },
      },
      {
        key: 'is_include_case',
        title: 'ケース同梱',
        width: 100,
        align: 'center',
        render: (_, record) => {
          const row = rowState[record.id] ?? buildInitialRowState();
          return (
            <AppCheckbox
              checked={row.isIncludeCase}
              onChange={e => handleIncludeCaseCheck(record.id, e.target.checked)}
              disabled={!row.checked}
              aria-label="ケース同梱"
            />
          );
        },
      },
      {
        key: 'delivery_fee',
        title: '送料',
        width: 100,
        render: (_, record) => {
          const row = rowState[record.id] ?? buildInitialRowState();
          return row.deliveryFee > 0 ? `${row.deliveryFee.toLocaleString()}円` : '';
        },
      },
      {
        key: 'remarks',
        title: '備考',
        render: (_, record) => {
          const row = rowState[record.id] ?? buildInitialRowState();
          return (
            <AppTextField
              value={row.remarks}
              onChange={e => handleRemarksChange(record.id, e.target.value)}
              disabled={!row.checked}
              count={{ max: 255, show: false }}
              className="w-full"
            />
          );
        },
      },
    ],
    [
      rowState,
      buildInitialRowState,
      handleSheetCheck,
      handleQuantityChange,
      handleDeliveryDateChange,
      handleIncludeCaseCheck,
      handleRemarksChange,
    ]
  );

  const tableSummary = (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={2}>
          合計
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <span id="subtotal_html">{subtotal.toLocaleString()}</span>円
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} colSpan={5} className="overflow-hidden" />
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <AppTable<RezeptSheetItem>
      columns={columns}
      dataSource={sheets}
      rowKey="id"
      pagination={false}
      scroll={{ x: 960 }}
      summary={sheets.length > 0 ? () => tableSummary : undefined}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="データがありません。"
          />
        ),
      }}
    />
  );
}
